/**
 * Structural legality and candidate lists (issue #9). A candidate that is not structurally legal
 * is not offered: feat level above the Level, wrong category for the slot, missing class or
 * ancestry trait, a dedication out of turn, an archetype feat without its dedication, a feat
 * taken as often as it may be, a Level-1-only feat later. Legacy candidates are legal
 * (docs/design.md) and left out only by the browsing filter, with their count reported so the
 * per-Choice "show Legacy" affordance can appear. Textual prerequisites ride along as status.
 */
import type { IndexRecord } from "../../shared/snapshot-format";
import type { Character, Choice, FoundationKind, LevelNumber, ProgressionSlot } from "../character/model";
import { choiceKey, findChoice, foundation } from "../character/model";
import { skillPotencies, skillPotencyStep, type SkillPotencyAnswer } from "../engine/abp";
import { recordMaxTakable } from "../engine/catalogue";
import { FAMILIAR_PACK, MASTER_ABILITY_SLUGS } from "../engine/familiar";
import { APPARITION_TAG } from "../engine/spellcasting";
import { SKILLS, SKILL_SLUGS, type Attribute, type Rank } from "../engine/values";
import type { GuideContext } from "./context";
import { levelOf } from "./context";
import { evaluatePrerequisites, overallStatus, type PrerequisiteStatus, type PrerequisiteVerdict } from "./prerequisites";

export interface EntryCandidate {
  uuid: string;
  name: string;
  type: string;
  /** A feat's category (class, ancestry, skill, general); null for other types. */
  category: string | null;
  level: number | null;
  traits: string[];
  rarity: string;
  remaster: boolean;
  publication: string;
  prerequisites: PrerequisiteVerdict[];
  status: PrerequisiteStatus | "none";
  /** This candidate fills the Choice now. */
  current: boolean;
}

export interface CandidateList {
  candidates: EntryCandidate[];
  /** Legal Legacy candidates the browsing filter left out. */
  hiddenLegacy: number;
  note: string | null;
}

export interface ListOptions {
  /** Include Legacy candidates (the global toggle, or this Choice's own). */
  legacy: boolean;
}

/* ------------------------------- held feats ------------------------------- */

interface HeldFeat {
  record: IndexRecord;
  key: string;
  level: LevelNumber;
  granted: boolean;
}

/** Every feat the Character holds (grants included), with the Choice that holds it. */
export function heldFeats(ctx: GuideContext): HeldFeat[] {
  const out: HeldFeat[] = [];
  for (const item of ctx.sheet.items) {
    if (item.type !== "feat" || !item.choiceKey || item.inMemory || item.sessionId) continue;
    const record = ctx.catalogue.byUuid(item.uuid);
    if (!record) continue;
    out.push({ record, key: item.choiceKey, level: levelOf(item.choiceKey), granted: item.grantedBy !== null });
  }
  return out;
}

/** Foundry's `timesTaken`: feats of the same slug and category, not counting the Choice being filled. */
function timesTaken(held: HeldFeat[], record: IndexRecord, exceptKey: string | null): number {
  return held.filter((h) => h.key !== exceptKey && h.record.s === record.s && h.record.c === record.c).length;
}

interface DedicationState {
  /** Held dedication feats, oldest first. */
  dedications: HeldFeat[];
  /** Whether a new dedication may be taken: every held dedication has two other feats of its archetype. */
  newDedicationAllowed: boolean;
  /** Lower-cased dedication names, to tell an archetype feat's dedication. */
  names: string[];
}

function mentions(record: IndexRecord, name: string): boolean {
  return (record.pq ?? []).some((p) => p.toLowerCase().includes(name));
}

/**
 * The dedication rule (Player Core, "Archetypes"): another dedication feat cannot be taken until
 * two other feats of the archetype are held. Archetype feats are attributed to a dedication by
 * naming it in their prerequisites; grants of the dedication itself do not count.
 */
export function dedicationState(ctx: GuideContext, exceptKey: string | null): DedicationState {
  const held = heldFeats(ctx).filter((h) => h.key !== exceptKey);
  const dedications = held.filter((h) => h.record.tr.includes("dedication")).sort((a, b) => a.level - b.level);
  const names = dedications.map((d) => d.record.n.toLowerCase());
  const newDedicationAllowed = dedications.every((d) => {
    const name = d.record.n.toLowerCase();
    const others = held.filter((h) => h !== d && !h.granted && h.record.tr.includes("archetype") && !h.record.tr.includes("dedication") && mentions(h.record, name));
    return others.length >= 2;
  });
  return { dedications, newDedicationAllowed, names };
}

/* --------------------------------- feats --------------------------------- */

/** Which feat categories a slot accepts (Foundry's feat groups: general slots also take skill feats). */
export function slotCategories(slot: ProgressionSlot): string[] {
  switch (slot) {
    case "ancestry-feat":
    case "ancestry-paragon-feat":
      return ["ancestry"];
    case "class-feat":
    case "archetype-feat":
    case "dual-class-feat":
      return ["class"];
    case "skill-feat":
      return ["skill"];
    case "general-feat":
      return ["general", "skill"];
    default:
      return [];
  }
}

export interface FeatRuling {
  legal: boolean;
  reason: string | null;
}

/**
 * Whether a feat record may fill a progression Choice at a Level. `exceptKey` is the Choice being
 * filled (its current Selection does not count against itself).
 */
export function featRuling(ctx: GuideContext, slot: ProgressionSlot, level: LevelNumber, record: IndexRecord, exceptKey: string | null, dedication = dedicationState(ctx, exceptKey), held = heldFeats(ctx)): FeatRuling {
  if (record.t !== "feat") return { legal: false, reason: "not a feat" };
  const categories = slotCategories(slot);
  if (!categories.includes(record.c ?? "")) return { legal: false, reason: `a ${record.c ?? "feature"} feat cannot fill a ${slot.replace(/-/g, " ")}` };
  if (typeof record.l === "number" && record.l > level) return { legal: false, reason: `level ${record.l} is above Level ${level}` };
  if (record.o1 && level !== 1) return { legal: false, reason: "can only be taken at Level 1" };
  if (timesTaken(held, record, exceptKey) >= recordMaxTakable(record)) return { legal: false, reason: "already taken as often as allowed" };
  const traits = record.tr;
  const isDedication = traits.includes("dedication");
  const isArchetype = traits.includes("archetype");
  if (slot === "ancestry-feat" || slot === "ancestry-paragon-feat") {
    if (ctx.ancestryTraits.length === 0) return { legal: false, reason: "no ancestry chosen" };
    if (!traits.some((t) => ctx.ancestryTraits.includes(t))) return { legal: false, reason: `not a ${ctx.ancestryTraits.join(" or ")} feat` };
    return { legal: true, reason: null };
  }
  if (isDedication) {
    if (slot === "skill-feat" || slot === "general-feat") return { legal: false, reason: "a dedication is a class feat" };
    if (!dedication.newDedicationAllowed) return { legal: false, reason: "two other feats of the current archetype are needed before another dedication" };
    if (level < 2) return { legal: false, reason: "dedications start at Level 2" };
    return { legal: true, reason: null };
  }
  if (isArchetype) {
    if (dedication.dedications.length === 0) return { legal: false, reason: "no dedication held" };
    const namesDedication = (record.pq ?? []).some((p) => /dedication/i.test(p));
    if (namesDedication && !dedication.names.some((n) => mentions(record, n))) return { legal: false, reason: "its dedication is not held" };
    return { legal: true, reason: null };
  }
  if (slot === "archetype-feat") return { legal: false, reason: "only archetype feats fill a Free Archetype slot" };
  if (slot === "class-feat" || slot === "dual-class-feat") {
    // Dual Class (#12): a class feat may come from either class.
    const classes = [ctx.classSlug, ctx.dualClassSlug].filter((c): c is string => !!c);
    if (classes.length === 0) return { legal: false, reason: "no class chosen" };
    if (!classes.some((c) => traits.includes(c))) return { legal: false, reason: `not a ${classes.join(" or ")} feat` };
  }
  return { legal: true, reason: null };
}

function candidateOf(ctx: GuideContext, record: IndexRecord, current: string | null): EntryCandidate {
  const prerequisites = record.t === "feat" ? evaluatePrerequisites(record.pq ?? [], ctx.prerequisites) : [];
  return {
    uuid: record.u,
    name: record.n,
    type: record.t,
    category: record.c ?? null,
    level: record.l,
    traits: [...record.tr],
    rarity: record.r,
    remaster: record.rm,
    publication: record.pub,
    prerequisites,
    status: overallStatus(prerequisites),
    current: record.u === current,
  };
}

function finish(ctx: GuideContext, records: IndexRecord[], current: string | null, options: ListOptions, note: string | null = null): CandidateList {
  let hiddenLegacy = 0;
  const kept: IndexRecord[] = [];
  for (const r of records) {
    if (!options.legacy && !r.rm && r.u !== current) hiddenLegacy++;
    else kept.push(r);
  }
  const candidates = kept.map((r) => candidateOf(ctx, r, current)).sort((a, b) => (a.level ?? 0) - (b.level ?? 0) || a.name.localeCompare(b.name));
  return { candidates, hiddenLegacy, note: note ?? (records.length === 0 ? "no legal candidate: the Choice stays open" : null) };
}

/** The feats that may fill a progression Choice. */
export function featCandidates(ctx: GuideContext, key: string, options: ListOptions): CandidateList {
  const choice = findChoice(ctx.character, key);
  const level = levelOf(key);
  if (!choice || choice.source.kind !== "progression") return { candidates: [], hiddenLegacy: 0, note: `no progression Choice ${key}` };
  const slot = choice.source.slot;
  const current = choice.selection?.uuid ?? null;
  const dedication = dedicationState(ctx, key);
  const held = heldFeats(ctx);
  const records = ctx.catalogue.records.filter((r) => r.t === "feat" && featRuling(ctx, slot, level, r, key, dedication, held).legal);
  return finish(ctx, records, current, options);
}

/* ---------------------------- Apparitions ---------------------------- */

/** Every apparition attuned already: the other apparition Choices' Selections (no duplicates). */
function heldApparitions(ctx: GuideContext, exceptKey: string | null): Set<string> {
  const held = new Set<string>();
  for (const level of ctx.character.levels) {
    for (const choice of level.choices) {
      if (choice.source.kind !== "progression" || choice.source.slot !== "apparition" || !choice.selection) continue;
      if (choiceKey(level.number, choice.source) === exceptKey) continue;
      held.add(choice.selection.uuid);
    }
  }
  return held;
}

/** Whether a record may fill an apparition Choice (#21): an apparition class feature not attuned already. */
export function apparitionRuling(ctx: GuideContext, record: IndexRecord, exceptKey: string | null, held = heldApparitions(ctx, exceptKey)): FeatRuling {
  if (record.p !== "classfeatures" || !(record.tg ?? []).includes(APPARITION_TAG)) return { legal: false, reason: "not an apparition" };
  if (held.has(record.u)) return { legal: false, reason: "attuned already" };
  return { legal: true, reason: null };
}

/** The apparitions that may fill an `apparition` Choice. */
export function apparitionCandidates(ctx: GuideContext, key: string, options: ListOptions): CandidateList {
  const choice = findChoice(ctx.character, key);
  if (!choice || choice.source.kind !== "progression" || choice.source.slot !== "apparition") return { candidates: [], hiddenLegacy: 0, note: `no apparition Choice ${key}` };
  const current = choice.selection?.uuid ?? null;
  const held = heldApparitions(ctx, key);
  const records = ctx.catalogue.records.filter((r) => apparitionRuling(ctx, r, key, held).legal);
  return finish(ctx, records, current, options);
}

/* ---------------------------- Familiar abilities ---------------------------- */

/** Every ability the Familiar holds already: the other Choices' Selections and the fixed abilities (no duplicates, Player Core). */
function heldFamiliarAbilities(ctx: GuideContext, exceptKey: string | null): Map<string, string> {
  const held = new Map<string, string>();
  for (const ability of ctx.sheet.familiar?.abilities ?? []) {
    if (!ability.uuid || ability.choiceKey === exceptKey) continue;
    held.set(ability.uuid, ability.fixed ? `fixed by ${ability.fixed.sourceName}` : "chosen already");
  }
  return held;
}

/** Whether a record may fill a Familiar ability Choice (#13): an Entry of the familiar-abilities Pack the Familiar does not hold already. */
export function familiarAbilityRuling(ctx: GuideContext, record: IndexRecord, exceptKey: string | null, held = heldFamiliarAbilities(ctx, exceptKey)): FeatRuling {
  if (record.p !== FAMILIAR_PACK) return { legal: false, reason: "not a Familiar ability" };
  const reason = held.get(record.u);
  if (reason) return { legal: false, reason };
  return { legal: true, reason: null };
}

/** The Familiar abilities that may fill a `familiar-ability` Choice, master abilities marked by category. */
export function familiarAbilityCandidates(ctx: GuideContext, key: string, options: ListOptions): CandidateList {
  const choice = findChoice(ctx.character, key);
  if (!choice || choice.source.kind !== "progression" || choice.source.slot !== "familiar-ability") return { candidates: [], hiddenLegacy: 0, note: `no Familiar ability Choice ${key}` };
  if (!ctx.sheet.familiar) return { candidates: [], hiddenLegacy: 0, note: "nothing gives this Character a Familiar" };
  const current = choice.selection?.uuid ?? null;
  const held = heldFamiliarAbilities(ctx, key);
  const records = ctx.catalogue.records.filter((r) => familiarAbilityRuling(ctx, r, key, held).legal);
  const list = finish(ctx, records, current, options);
  for (const candidate of list.candidates) candidate.category = MASTER_ABILITY_SLUGS.has(ctx.catalogue.byUuid(candidate.uuid)?.s ?? "") ? "master" : "familiar";
  list.candidates.sort((a, b) => (a.category === b.category ? 0 : a.category === "master" ? 1 : -1) || a.name.localeCompare(b.name));
  return list;
}

/* ------------------------------ foundations ------------------------------ */

export function foundationRuling(ctx: GuideContext, kind: FoundationKind, record: IndexRecord): FeatRuling {
  if (kind === "dual-class") {
    // The Dual Class second class (#12): any class other than the first.
    if (record.t !== "class") return { legal: false, reason: "not a class" };
    const first = foundation(ctx.character, "class")?.uuid;
    if (!first) return { legal: false, reason: "no class chosen" };
    if (record.u === first) return { legal: false, reason: "already the first class" };
    return { legal: true, reason: null };
  }
  if (record.t !== kind) return { legal: false, reason: `not a ${kind}` };
  if (kind === "heritage") {
    const ancestry = foundation(ctx.character, "ancestry")?.uuid;
    const slug = ancestry ? ctx.entries.get(ancestry)?.slug ?? ctx.catalogue.byUuid(ancestry)?.s ?? null : null;
    if (!slug) return { legal: false, reason: "no ancestry chosen" };
    if (record.a !== null && record.a !== undefined && record.a !== slug) return { legal: false, reason: `a ${record.a} heritage` };
  }
  return { legal: true, reason: null };
}

/** Ancestries, heritages of the chosen ancestry (versatile ones included), backgrounds, classes, the Dual Class second class. */
export function foundationCandidates(ctx: GuideContext, kind: FoundationKind, options: ListOptions): CandidateList {
  const current = foundation(ctx.character, kind)?.uuid ?? null;
  const records = ctx.catalogue.records.filter((r) => foundationRuling(ctx, kind, r).legal);
  const note = records.length === 0 ? (kind === "heritage" ? "choose an ancestry first" : kind === "dual-class" ? "choose the first class first" : null) : null;
  return finish(ctx, records, current, options, note);
}

/* --------------------------------- boosts -------------------------------- */

export interface AttributeCandidate {
  attribute: Attribute;
  label: string;
  legal: boolean;
  reason: string | null;
  /** The modifier is +4 or more already: this boost is partial (two partial boosts make +1). */
  partial: boolean;
  current: boolean;
}

/** The attributes a free boost at a Level may go to: not one boosted already in the same set. */
export function boostCandidates(ctx: GuideContext, key: string): AttributeCandidate[] {
  const choice = findChoice(ctx.character, key);
  const level = levelOf(key);
  const ordinal = choice?.source.kind === "progression" ? choice.source.ordinal : 0;
  const current = typeof choice?.answers?.boost === "string" ? choice.answers.boost : null;
  const bucket = ctx.track.boosts.filter((b) => sameBucket(b.level, level) && !(b.level === level && b.ordinal === ordinal));
  const taken = new Set(bucket.map((b) => b.attribute));
  return (["str", "dex", "con", "int", "wis", "cha"] as Attribute[]).map((attribute) => ({
    attribute,
    label: LABELS[attribute],
    legal: !taken.has(attribute),
    reason: taken.has(attribute) ? "boosted already in this set" : null,
    partial: ctx.track.isPartial(level, ordinal, attribute),
    current: current === attribute,
  }));
}

function sameBucket(a: LevelNumber, b: LevelNumber): boolean {
  const bucketOf = (l: LevelNumber): number => [1, 5, 10, 15, 20].find((x) => x >= l) ?? 20;
  return bucketOf(a) === bucketOf(b);
}

const LABELS: Record<Attribute, string> = { str: "Strength", dex: "Dexterity", con: "Constitution", int: "Intelligence", wis: "Wisdom", cha: "Charisma" };

/** A foundation's boost slot: the ancestry's or background's `boost:<n>`, from the Entry's slot options, distinct from its other boosts and flaws. */
export function slotBoostCandidates(ctx: GuideContext, kind: "ancestry" | "background", flag: string): AttributeCandidate[] {
  const choice = findChoice(ctx.character, choiceKey(1, { kind }));
  const uuid = choice?.selection?.uuid;
  const entry = uuid ? ctx.entries.get(uuid) : undefined;
  const slots = slotOptions(entry?.system.boosts);
  const index = Number(flag.split(":")[1]);
  const options = slots[index] ?? [];
  const answers = choice?.answers ?? {};
  const alternate = kind === "ancestry" && answers.alternateBoosts === true;
  const current = typeof answers[flag] === "string" ? answers[flag] : null;
  // The other slots' attributes (fixed or answered) and the flaws are taken.
  const taken = new Set<string>();
  if (alternate) {
    for (const k of ["alternate:0", "alternate:1"]) if (k !== flag && typeof answers[k] === "string") taken.add(answers[k] as string);
  } else {
    slots.forEach((opts, i) => {
      if (i === index) return;
      if (opts.length === 1) taken.add(opts[0]!);
      else if (typeof answers[`boost:${i}`] === "string") taken.add(answers[`boost:${i}`] as string);
    });
  }
  for (const opts of slotOptions(entry?.system.flaws)) if (opts.length === 1) taken.add(opts[0]!);
  for (const k of ["voluntary:flaw:0", "voluntary:flaw:1"]) if (typeof answers[k] === "string") taken.add(answers[k] as string);
  const offered = alternate || /^alternate:/.test(flag) ? (["str", "dex", "con", "int", "wis", "cha"] as Attribute[]) : options;
  return offered.map((attribute) => ({
    attribute,
    label: LABELS[attribute],
    legal: !taken.has(attribute),
    reason: taken.has(attribute) ? "boosted or flawed already by this Entry" : null,
    partial: false,
    current: current === attribute,
  }));
}

/** The attribute lists of an Entry's boost or flaw slots, by index. */
export function slotOptions(record: unknown): Attribute[][] {
  if (typeof record !== "object" || record === null) return [];
  return Object.entries(record as Record<string, { value?: unknown }>)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, slot]) => (Array.isArray(slot?.value) ? (slot.value as unknown[]).filter((v): v is Attribute => typeof v === "string" && v in LABELS) : []));
}

/** The class's key attribute options. */
export function keyAbilityCandidates(ctx: GuideContext): AttributeCandidate[] {
  const choice = findChoice(ctx.character, choiceKey(1, { kind: "class" }));
  const current = typeof choice?.answers?.keyAbility === "string" ? choice.answers.keyAbility : null;
  const options = [...new Set([...ctx.tables.keyOptions, ...ctx.state.actor.system.build.attributes.keyOptions])];
  return options.map((attribute) => ({ attribute, label: LABELS[attribute], legal: true, reason: null, partial: false, current: current === attribute }));
}

/* --------------------------------- skills -------------------------------- */

export interface SkillCandidate {
  slug: string;
  label: string;
  lore: boolean;
  /** The rank before this Choice's own training. */
  rank: Rank;
  legal: boolean;
  reason: string | null;
  current: boolean;
}

/** The rank cap a skill increase at a Level may reach: expert from 3, master from 7, legendary from 15 (Player Core). */
export function increaseCap(level: LevelNumber): Rank {
  return level >= 15 ? 4 : level >= 7 ? 3 : 2;
}

/** Skill increases at or after a Choice (later ordinals at the same Level count), by skill, so the rank before it can be read off the sheet. */
function laterIncreases(ctx: GuideContext, level: LevelNumber, ordinal: number): Map<string, number> {
  const out = new Map<string, number>();
  for (const lvl of ctx.character.levels) {
    if (lvl.number < level || lvl.number > ctx.character.level) continue;
    for (const c of lvl.choices) {
      if (c.source.kind !== "progression" || c.source.slot !== "skill-increase") continue;
      if (lvl.number === level && c.source.ordinal < ordinal) continue;
      const skill = c.answers?.skill;
      if (typeof skill === "string" && skill) out.set(skill, (out.get(skill) ?? 0) + 1);
    }
  }
  return out;
}

/** The skills a skill increase at a Level may raise: below the Level's cap, and untrained only for an Intelligence-opened increase. */
export function skillIncreaseCandidates(ctx: GuideContext, key: string, intelligence: boolean): SkillCandidate[] {
  const choice = findChoice(ctx.character, key);
  const level = levelOf(key);
  const ordinal = choice?.source.kind === "progression" ? choice.source.ordinal : 0;
  const current = typeof choice?.answers?.skill === "string" ? choice.answers.skill : null;
  const later = laterIncreases(ctx, level, ordinal);
  const cap = increaseCap(level);
  return allSkills(ctx).map(({ slug, label, lore }) => {
    const rank = Math.max(0, (ctx.sheet.skills[slug]?.rank ?? 0) - (later.get(slug) ?? 0)) as Rank;
    const reason = intelligence && rank > 0 ? "an Intelligence increase trains an untrained skill" : rank >= cap ? `${RANK_NAMES[rank]} already; ${RANK_NAMES[cap]} is the most at Level ${level}` : null;
    return { slug, label, lore, rank, legal: reason === null, reason, current: current === slug };
  });
}

/** A class skill training at Level 1 (`skill:<n>`): any skill not trained already. */
export function skillTrainingCandidates(ctx: GuideContext, flag: string): SkillCandidate[] {
  const choice = findChoice(ctx.character, choiceKey(1, { kind: "class" }));
  const current = typeof choice?.answers?.[flag] === "string" ? (choice.answers[flag] as string) : null;
  return allSkills(ctx).map(({ slug, label, lore }) => {
    const rank = (ctx.sheet.skills[slug]?.rank ?? 0) as Rank;
    const own = current === slug;
    const trained = rank > 0 && !own;
    return { slug, label, lore, rank: own ? 0 : rank, legal: !trained, reason: trained ? "trained already" : null, current: own };
  });
}

/* ------------------------- Automatic Bonus Progression ------------------------- */

/** Every skill potency answer in the log (Levels above the Character's included, for planning ahead). */
export function skillPotencyAnswers(character: Character): SkillPotencyAnswer[] {
  const out: SkillPotencyAnswer[] = [];
  for (const level of character.levels) {
    for (const c of level.choices) {
      if (c.source.kind !== "progression" || c.source.slot !== "skill-potency") continue;
      const skill = c.answers?.skill;
      out.push({ level: level.number, ordinal: c.source.ordinal, skill: typeof skill === "string" ? skill : "" });
    }
  }
  return out;
}

/** The potencies by skill before a skill potency Choice (earlier Levels and earlier ordinals at its Level). */
export function skillPotenciesBefore(ctx: GuideContext, level: LevelNumber, ordinal: number): Map<string, number> {
  return skillPotencies(skillPotencyAnswers(ctx.character), { level, ordinal });
}

/**
 * The skills a skill potency Choice may name (Automatic Bonus Progression, #12): for a new
 * potency, a skill without one; for a raise, a skill at exactly the step's starting potency.
 */
export function skillPotencyCandidates(ctx: GuideContext, key: string): SkillCandidate[] {
  const choice = findChoice(ctx.character, key);
  const level = levelOf(key);
  const ordinal = choice?.source.kind === "progression" ? choice.source.ordinal : 0;
  const current = typeof choice?.answers?.skill === "string" ? choice.answers.skill : null;
  const step = skillPotencyStep(level, ordinal);
  const before = skillPotenciesBefore(ctx, level, ordinal);
  return allSkills(ctx).map(({ slug, label, lore }) => {
    const potency = before.get(slug) ?? 0;
    const reason = !step ? "no skill potency step at this Level" : step.kind === "new" ? (potency > 0 ? `already has a +${potency} potency` : null) : potency !== step.from ? (potency === 0 ? "no potency to raise" : `at +${potency}, not +${step.from}`) : null;
    return { slug, label, lore, rank: (ctx.sheet.skills[slug]?.rank ?? 0) as Rank, legal: reason === null, reason, current: current === slug };
  });
}

/** The attribute the apex at 17 may raise (Automatic Bonus Progression, #12): any of the six. */
export function apexCandidates(ctx: GuideContext, key: string): AttributeCandidate[] {
  const choice = findChoice(ctx.character, key);
  const current = typeof choice?.answers?.attribute === "string" ? choice.answers.attribute : null;
  return (["str", "dex", "con", "int", "wis", "cha"] as Attribute[]).map((attribute) => ({ attribute, label: LABELS[attribute], legal: true, reason: null, partial: false, current: current === attribute }));
}

function allSkills(ctx: GuideContext): { slug: string; label: string; lore: boolean }[] {
  const core = SKILL_SLUGS.map((slug) => ({ slug, label: SKILLS[slug].label, lore: false }));
  const lores = Object.entries(ctx.sheet.skills).filter(([, s]) => s.lore).map(([slug, s]) => ({ slug, label: s.label, lore: true }));
  return [...core, ...lores];
}

const RANK_NAMES = ["untrained", "trained", "expert", "master", "legendary"] as const;

/* ------------------------------ legality checks ------------------------------ */

/** Whether a filled progression or foundation Choice is still structurally legal; null when it is not something the guide rules on. */
export function selectionRuling(ctx: GuideContext, level: LevelNumber, choice: Choice): FeatRuling | null {
  const uuid = choice.selection?.uuid;
  if (!uuid) return null;
  const record = ctx.catalogue.byUuid(uuid);
  if (!record) return null;
  const source = choice.source;
  const key = choiceKey(level, source);
  if (source.kind === "ancestry" || source.kind === "heritage" || source.kind === "background" || source.kind === "class" || source.kind === "dual-class") return foundationRuling(ctx, source.kind, record);
  if (source.kind === "progression" && slotCategories(source.slot).length > 0) return featRuling(ctx, source.slot, level, record, key);
  if (source.kind === "progression" && source.slot === "familiar-ability") {
    // A slot its granting Entry no longer opens reopens its ability (the issue's "over-committed" case), unlike a feat slot from a former class.
    if (ctx.extra.has(key)) return { legal: false, reason: "no Familiar ability slot opens this Choice any more" };
    return familiarAbilityRuling(ctx, record, key);
  }
  if (source.kind === "progression" && source.slot === "apparition") {
    if (ctx.extra.has(key)) return { legal: false, reason: "no apparition slot opens this Choice any more" };
    return apparitionRuling(ctx, record, key);
  }
  return null;
}
